<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue';

import { fetchTicketMessages, sendMessage } from '@/api/tickets';
import AttachButton from '@/components/AttachButton.vue';
import AttachmentChips from '@/components/AttachmentChips.vue';
import MessageAttachments from '@/components/MessageAttachments.vue';
import { useAttachments } from '@/composables/useAttachments';
import { usePolling } from '@/composables/usePolling';
import { useThreadReadReceipts } from '@/composables/useThreadReadReceipts';
import { formatRelativeTime } from '@/tools/format';
import { isOwnMessage } from '@/tools/messages';
import type { WidgetMessage, WidgetTicket } from '@/api/types';

const props = defineProps<{
  ticket: WidgetTicket;
}>();

const messages = ref<WidgetMessage[]>([]);
const loading = ref(true);
const loadError = ref<string | null>(null);

const draft = ref('');
const sending = ref(false);
const sendError = ref<string | null>(null);

const attachments = useAttachments();

const thread = ref<HTMLElement | null>(null);

useThreadReadReceipts(() => props.ticket.id, messages);

const scrollToLatest = async (): Promise<void> => {
  await nextTick();

  if (thread.value) {
    thread.value.scrollTop = thread.value.scrollHeight;
  }
};

const load = async (): Promise<void> => {
  loading.value = true;
  loadError.value = null;

  try {
    messages.value = await fetchTicketMessages(props.ticket.id);

    // Before the scroll, not in the `finally` after it. The thread is rendered
    // only once this is false, so scrolling first moved a "Loading…" line and
    // left the conversation open at its top — with the newest turn, the one a
    // visitor came back for, below the fold of anything longer than the panel.
    // That turn is what the read receipt then names, so "drawn" has to mean
    // "on screen".
    loading.value = false;

    await scrollToLatest();
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : 'Could not load this conversation.';
  } finally {
    loading.value = false;
  }
};

/**
 * The thread as it stands once `sent` is in it.
 *
 * Read back from the API rather than appended to what is on screen. A reply
 * an agent wrote in the last few seconds — or one held back because the visitor
 * had scrolled up — may not be in the list yet, and the moment they press send
 * is the moment they are looking at the conversation. Appending would leave that reply out and the
 * visitor answering a message they have not been shown.
 *
 * If the read fails the send still happened, so the message is appended after
 * all: a thread missing somebody else's reply is a smaller lie than a reply the
 * visitor just sent not appearing.
 */
const threadAfterSending = async (sent: WidgetMessage): Promise<WidgetMessage[]> => {
  try {
    return await fetchTicketMessages(props.ticket.id);
  } catch {
    return [...messages.value, sent];
  }
};

const submit = async (): Promise<void> => {
  const body = draft.value.trim();

  // A screenshot needs no caption: either half is enough to send.
  if ((!body && attachments.pending.value.length === 0) || sending.value) {
    return;
  }

  sending.value = true;
  sendError.value = null;

  try {
    // The images first. If one cannot be stored nothing is sent, and what was
    // typed and chosen stays in the composer for another try.
    const attachmentIds = await attachments.upload();

    // No optimistic append: without a cache to reconcile it against, showing a
    // message that the server may still reject is a lie the skeleton can skip.
    const message = await sendMessage(props.ticket.id, body, attachmentIds);

    draft.value = '';
    attachments.clear();
    messages.value = await threadAfterSending(message);
    // The whole thread was just re-read and is about to be scrolled to its
    // end, so nothing is waiting any more.
    waiting.value = [];

    await scrollToLatest();
  } catch (error) {
    sendError.value = error instanceof Error ? error.message : 'Could not send your reply.';
  } finally {
    sending.value = false;
  }
};

/**
 * How often an open thread asks whether anything new was said. Short enough
 * that a reply feels like it arrived rather than was fetched; long enough that
 * it takes a quarter of the visitor's rate limit (15 of 60 calls a minute),
 * leaving the rest for read reports, the list and what they send.
 */
const REFRESH_INTERVAL_MS = 4_000;

/** Within this many pixels of the end counts as "reading the latest". */
const NEAR_END_PX = 80;

/**
 * Replies that have arrived but are not drawn, because the visitor is reading
 * further up. They are offered ("New reply") rather than appended.
 *
 * Not a nicety. Whatever is in `messages` is what the read receipt reports as
 * shown, and a reply reported as read is a reply that is not emailed. Appending
 * one below the fold of somebody rereading the start of the thread would tell
 * the API they had seen it, and they would then hear the answer by neither
 * channel. So "in `messages`" keeps meaning "on screen": a turn enters it only
 * when the thread is at its end, or when the visitor asks for it.
 */
const waiting = ref<WidgetMessage[]>([]);

const isAtEnd = (): boolean => {
  const element = thread.value;

  return !element
    || element.scrollHeight - element.scrollTop - element.clientHeight <= NEAR_END_PX;
};

const showWaiting = async (): Promise<void> => {
  const known = new Set(messages.value.map((message) => message.id));

  messages.value = [...messages.value, ...waiting.value.filter((message) => !known.has(message.id))];
  waiting.value = [];

  await scrollToLatest();
};

/** Coming back down to the end is asking for them, as much as the button is. */
const onThreadScroll = (): void => {
  if (waiting.value.length > 0 && isAtEnd()) {
    void showWaiting();
  }
};

/**
 * Bring in what was said since the thread was drawn.
 *
 * Adds, never replaces. The list on screen may hold a message the visitor sent
 * a moment ago that a response already in flight does not know about, so
 * swapping the array for the server's would make their own words vanish and
 * come back. Only turns whose id is not here yet are taken, in the order the
 * API gave them.
 *
 * Drawn at once for someone at the end of the conversation, and held in
 * `waiting` for someone who scrolled up — who is neither yanked away from what
 * they were reading nor recorded as having read what they were not shown.
 */
const refresh = async (): Promise<void> => {
  // Nothing to add to until the first load has drawn the thread — and a load
  // that failed has its own "Try again", which a silent refresh must not race.
  // Not while a send is in flight either: `submit` re-reads the whole thread
  // when it lands, and two writers to one list is how a turn is drawn twice.
  if (loading.value || loadError.value || sending.value) {
    return;
  }

  const latest = await fetchTicketMessages(props.ticket.id);

  if (sending.value) {
    return;
  }

  const known = new Set(messages.value.map((message) => message.id));
  const arrived = latest.filter((message) => !known.has(message.id));

  if (arrived.length === 0) {
    waiting.value = [];

    return;
  }

  waiting.value = arrived;

  if (isAtEnd()) {
    await showWaiting();
  }
};

usePolling(refresh, REFRESH_INTERVAL_MS);

onMounted(load);
</script>

<template>
  <div class="screen">
    <div
      ref="thread"
      class="thread"
      @scroll.passive="onThreadScroll"
    >
      <p
        v-if="loading"
        class="hint"
      >
        Loading…
      </p>

      <template v-else-if="loadError">
        <p class="error">
          {{ loadError }}
        </p>
        <button
          type="button"
          class="button button--quiet"
          @click="load"
        >
          Try again
        </button>
      </template>

      <p
        v-else-if="messages.length === 0"
        class="hint"
      >
        No messages yet.
      </p>

      <template v-else>
        <div
          v-for="message in messages"
          :key="message.id"
          class="message"
          :class="{ 'message--own': isOwnMessage(message) }"
          data-testid="widget-message"
        >
          <!-- A turn that is only a file has no words to put in a bubble. -->
          <div
            v-if="message.body"
            class="message__bubble"
          >
            {{ message.body }}
          </div>
          <MessageAttachments
            v-if="message.attachments.length"
            :ticket-id="ticket.id"
            :attachments="message.attachments"
          />
          <div class="message__meta">
            <span v-if="!isOwnMessage(message)">{{ message.authorName ?? 'Support' }} · </span>
            <span>{{ formatRelativeTime(message.createdAt) }}</span>
          </div>
        </div>
      </template>
    </div>

    <button
      v-if="waiting.length"
      type="button"
      class="new-replies"
      data-testid="widget-new-replies"
      @click="showWaiting"
    >
      {{ waiting.length === 1 ? 'New reply' : `${waiting.length} new replies` }} ↓
    </button>

    <form
      class="composer"
      @submit.prevent="submit"
    >
      <p
        v-if="sendError ?? attachments.refusal.value"
        class="error"
        data-testid="widget-reply-error"
      >
        {{ sendError ?? attachments.refusal.value }}
      </p>

      <AttachmentChips
        :items="attachments.pending.value"
        :disabled="sending"
        @remove="attachments.remove"
      />

      <div class="composer__row">
        <AttachButton
          v-if="attachments.available.value"
          test-id="widget-reply-attach"
          :disabled="sending"
          @pick="attachments.add"
        />
        <textarea
          v-model="draft"
          class="field"
          rows="2"
          placeholder="Write a reply"
          maxlength="20000"
          :disabled="sending"
          data-testid="widget-reply-body"
          @keydown.enter.exact.prevent="submit"
        />
        <button
          type="submit"
          class="button"
          :disabled="sending || (!draft.trim() && !attachments.pending.value.length)"
          data-testid="widget-reply-submit"
        >
          {{ sending ? '…' : 'Send' }}
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
.screen {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.thread {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.message {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  max-width: 85%;
}

.message--own {
  align-self: flex-end;
  align-items: flex-end;
}

.message__bubble {
  padding: 8px 12px;
  border-radius: var(--w-radius);
  background: var(--w-bg-subtle);
  white-space: pre-wrap;
  word-break: break-word;
}

.message--own .message__bubble {
  background: var(--w-accent);
  color: var(--w-accent-text);
}

.message__meta {
  font-size: 11px;
  color: var(--w-text-muted);
}

.new-replies {
  align-self: center;
  margin: 0 0 8px;
  padding: 6px 12px;
  border: 1px solid var(--w-border);
  border-radius: 999px;
  background: var(--w-bg);
  box-shadow: var(--w-shadow);
  font-size: 12px;
  font-weight: 600;
}

.composer {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 16px;
  border-top: 1px solid var(--w-border);
}

.composer__row {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.field {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid var(--w-border);
  border-radius: 8px;
  background: var(--w-bg);
  outline: none;
  resize: none;
}

.field:focus {
  border-color: var(--w-accent);
}

.button {
  padding: 8px 14px;
  border: none;
  border-radius: 8px;
  background: var(--w-accent);
  color: var(--w-accent-text);
  font-weight: 600;
}

.button:disabled {
  opacity: 0.5;
  cursor: default;
}

.button--quiet {
  align-self: flex-start;
  background: var(--w-bg-subtle);
  color: var(--w-text);
}

.hint {
  margin: 0;
  font-size: 13px;
  color: var(--w-text-muted);
}

.error {
  margin: 0;
  font-size: 13px;
  color: var(--w-danger);
}
</style>
